import AsyncStorage from "@react-native-async-storage/async-storage";
import settings from "./settings";

/**
 * User preferences — pure client-side, persisted to AsyncStorage. Loaded
 * once at boot in App.js (`hydratePreferences`), then read directly from
 * the exported `preferences` object.
 *
 * Page size: mirrored into `settings.pageSizeLoad` on load and on update so
 * the existing controllers that read `settings.pageSizeLoad` directly stay
 * in sync without each one having to know about the preferences module.
 */

const STORAGE_KEY = "@maritime_preferences_v1";

// pageSize sources from settings so there's a single hardcoded value to
// edit. landingRoute has no settings equivalent — kept as the literal
// default here.
const DEFAULTS = {
  pageSize: settings.pageSizeLoad,
  landingRoute: "dashboard",
};

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const LANDING_ROUTE_OPTIONS = [
  { id: "dashboard", title: "Dashboard" },
  { id: "extension", title: "Extensions" },
  { id: "ringgroup", title: "Ring Groups" },
  { id: "ivr", title: "IVR Menus" },
  { id: "user", title: "Users" },
];

let cached = { ...DEFAULTS };

/** Read preferences from AsyncStorage and apply to live settings. */
export async function hydratePreferences() {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (PAGE_SIZE_OPTIONS.includes(parsed.pageSize)) {
        cached.pageSize = parsed.pageSize;
      }
      if (
        LANDING_ROUTE_OPTIONS.find((o) => o.id === parsed.landingRoute)
      ) {
        cached.landingRoute = parsed.landingRoute;
      }
    }
  } catch {
    // Fall back to defaults silently.
  }
  // Mirror page size into settings so existing controllers pick it up
  // without each one knowing about preferences.
  settings.pageSizeLoad = cached.pageSize;
  return cached;
}

async function persist() {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(cached));
  } catch {
    // Persistence failure is non-fatal — in-memory cache still applies.
  }
}

export function getPreferences() {
  return cached;
}

export async function setPageSize(value) {
  if (!PAGE_SIZE_OPTIONS.includes(value)) return;
  cached.pageSize = value;
  settings.pageSizeLoad = value;
  await persist();
}

export async function setLandingRoute(value) {
  if (!LANDING_ROUTE_OPTIONS.find((o) => o.id === value)) return;
  cached.landingRoute = value;
  await persist();
}
